import { type ChangeEvent, Component, type FocusEvent, type KeyboardEvent, type ReactElement, type ReactNode } from 'react';
import { Key } from 'ts-keycode-enum';
import { debounce } from 'ts-debounce';
import cn from 'classnames';

import { disableBodyScroll, enableBodyScroll } from 'shared/util/bodyScroll';
import { SpinnerIcon } from 'shared/icons/SpinnerIcon';

import type { ApiResponse, AutocompleteDestination } from '@/interfaces';

import styles from './Autocomplete.module.css';

export interface AutocompleteProps {
    id: string;
    label: string;
    value: string;
    fetchOptions: (searchTerm: string) => Promise<ApiResponse<AutocompleteDestination[]>>;
    onClick: (option?: AutocompleteDestination) => void; // Receives the selected option exactly as returned from mapResults
    onTextChange: (text: string) => void; // Must update the value or the field will be read only
    placeholder?: string;
    minimumCharacters?: number; // Minimum characters required to trigger a request
    errorText?: string;
    isMobile?: boolean;
}

export interface AutocompleteState {
    options: AutocompleteDestination[];
    selectedOption: number;
    focus: boolean;
    fetching: boolean;
    clearOverlayInput: boolean;
    shouldHaveOptions: boolean;
}

export class Autocomplete extends Component<AutocompleteProps, AutocompleteState> {
    constructor(props: AutocompleteProps) {
        super(props);
        this.state = {
            options: [],
            selectedOption: 0,
            focus: false,
            fetching: false,
            clearOverlayInput: false,
            shouldHaveOptions: false,
        };
    }

    componentWillUnmount(): void {
        window.removeEventListener('keydown', this.onKeyDown as unknown as EventListener);
    }

    selectCurrentOption = (): void => {
        const { options, selectedOption } = this.state;
        if (selectedOption >= 0 && selectedOption < options.length) {
            this.onClickOption(options[selectedOption]);
        } else {
            this.setState({ focus: false });
        }
    };

    onClickOption = (option: AutocompleteDestination): void => {
        this.props.onClick(option);
        this.setState({ focus: false, clearOverlayInput: false }, () => enableBodyScroll());
    };

    onChangeText = async ({ target: { value } }: ChangeEvent<HTMLInputElement>): Promise<void> => {
        this.props.onTextChange(value);
        this.setState({ focus: true, clearOverlayInput: false });

        if (value.length >= (this.props.minimumCharacters || 0)) {
            await debounce((v: string) => this.fetchAndSetOptions(v), 300)(value);
        } else {
            this.setState({
                options: this.props.isMobile ? this.state.options : [],
                fetching: false,
                shouldHaveOptions: false,
            });
        }
    };

    fetchAndSetOptions = async (searchTerm: string): Promise<void> => {
        this.setState({ fetching: true, focus: true });
        const result = await this.props.fetchOptions(searchTerm);
        if (result.status === 200 || result.status === 308) {
            const newOptions = Array.isArray(result.data) && result.data.length ? result.data : [];
            this.setState({
                options: newOptions,
                selectedOption: this.props.isMobile ? -1 : 0,
                shouldHaveOptions: true,
            });
        }
        this.setState({ fetching: false });
    };

    onKeyDown = (event: KeyboardEvent): void => {
        const { options, selectedOption } = this.state;
        switch (event.keyCode) {
            case Key.DownArrow:
                this.setState({ selectedOption: (selectedOption + 1) % options.length });
                event.preventDefault();
                break;

            case Key.UpArrow:
                this.setState({ selectedOption: selectedOption <= 0 ? options.length - 1 : selectedOption - 1 });
                event.preventDefault();
                break;

            case Key.Enter:
                this.selectCurrentOption();
                event.preventDefault();
                break;

            case Key.Tab:
                this.selectCurrentOption();
                break;

            case Key.Escape:
                this.setState({ selectedOption: 0, options: [] });
                break;
        }
    };

    onFocus = (event: FocusEvent<HTMLInputElement>): void => {
        disableBodyScroll();
        event.target.select();
        if (!this.props.isMobile) {
            window.addEventListener('keydown', this.onKeyDown as unknown as EventListener);
        }
        this.setState({ focus: true, clearOverlayInput: false });
    };

    onBlur = (): void => {
        if (!this.props.isMobile) {
            window.removeEventListener('keydown', this.onKeyDown as unknown as EventListener);
            this.selectCurrentOption();
        }
    };

    onClear = (): void => {
        if (this.props.isMobile) {
            this.setState({ clearOverlayInput: true });
        } else {
            this.props.onClick(undefined);
        }
    };

    onCancel = (): void => {
        this.setState({ focus: false });
        if (this.state.selectedOption === -1) {
            this.props.onClick();
        }
        enableBodyScroll();
    };

    renderOptions = (): ReactElement => {
        const { id, isMobile, value } = this.props;
        const { options, selectedOption, shouldHaveOptions, focus } = this.state;
        const onClickOptionFunc = (option) => (e) => {
            e.stopPropagation();
            this.onClickOption(option);
        };
        const onHover = (optionIdx) => () => this.setState({ selectedOption: optionIdx });

        if (options.length > 0 && focus) {
            return (
                <ul role={isMobile ? undefined : 'listbox'} id={`${id}-options`} data-testid={`${id}-options`}>
                    {options.map((option, optionIdx) => {
                        const key = option?.key;
                        return (
                            <li
                                key={`el${key}`}
                                role={isMobile ? undefined : 'none'}
                                className={`${selectedOption === optionIdx ? 'selected' : ''}`}
                                onClick={onClickOptionFunc(option)}
                                onMouseOver={onHover(optionIdx)}>
                                <a
                                    role={isMobile ? undefined : 'option'}
                                    aria-selected={isMobile ? undefined : selectedOption === optionIdx}
                                    id={`${id}-option${key}`}
                                    // biome-ignore lint/a11y/useValidAnchor: This existed before biome was added
                                    onClick={onClickOptionFunc(option)}
                                >
                                    {option.text}
                                </a>
                            </li>
                        );
                    })}
                </ul>
            );
        }
        if (shouldHaveOptions && focus) {
            return (
                <ul role={isMobile ? undefined : 'listbox'} id={`${id}-options`} data-testid={`${id}-options`}>
                    <li className="type u-stone u-mh0 u-mt8 u-mb2">Ingen treff for {value} funnet</li>
                </ul>
            );
        }
        return null;
    };

    renderSpinner = (): ReactElement => (
        <div className={`spinner ${this.state.focus && this.state.fetching ? 'active' : 'inactive'}`}>
            <SpinnerIcon className="u-bg-milk" />
        </div>
    );

    render(): ReactNode {
        const { id, label, value, placeholder = '', errorText = '', isMobile } = this.props;
        const { options, selectedOption, clearOverlayInput, focus } = this.state;

        const hasSelectedOption = options.length > 0 && focus && selectedOption !== -1;
        const hasText = value && value.length > 0;
        return (
            <div
                className={cn(styles.autocomplete, 'input input--text', {
                    'input--is-invalid': Boolean(errorText),
                })}>
                <label htmlFor={id} className="u-truncate">
                    {label}
                </label>
                <div
                    {...(!isMobile && {
                        role: 'combobox',
                        'aria-owns': `${id}-options`,
                        'aria-expanded': hasSelectedOption,
                    })}>
                    <input
                        type="text"
                        id={id}
                        aria-label={id}
                        className="u-size1of1 u-truncate"
                        value={value}
                        data-testid={id}
                        autoComplete="off"
                        onChange={this.onChangeText}
                        onFocus={this.onFocus}
                        onBlur={this.onBlur}
                        placeholder={placeholder}
                        {...(!isMobile && {
                            'aria-autocomplete': 'list',
                            'aria-activedescendant': hasSelectedOption ? `${id}-option${options[selectedOption]?.key}` : undefined,
                            'aria-controls': hasSelectedOption ? `${id}-options` : undefined,
                        })}
                    />
                    {hasText && !isMobile ? (
                        <button
                            type="button"
                            aria-label="Nullstill"
                            data-testid="resetAutocompleteButton"
                            className="input--search__button input--search__button--reset"
                            onClick={this.onClear.bind(this)}
                        />
                    ) : null}
                </div>
                {errorText ? (
                    <div className="input__sub-text">
                        <span className="input__sub-text__error">{errorText}</span>
                    </div>
                ) : null}
                {focus && isMobile ? (
                    <div className="u-pa16 overlay">
                        <div className="top-row">
                            <div className="input input--text u-float-left input-wrapper">
                                <label htmlFor={`${id}-mobile-overlay`} className="u-screen-reader-only">
                                    {label}
                                </label>
                                <input
                                    type="text"
                                    id={`${id}-mobile-overlay`}
                                    className="u-size1of1 u-pr32 u-truncate"
                                    value={clearOverlayInput ? '' : value}
                                    placeholder={placeholder}
                                    data-testid={`${id}-mobile-overlay`}
                                    autoComplete="off"
                                    onChange={this.onChangeText}
                                />
                                <button
                                    type="button"
                                    aria-label="Nullstill"
                                    data-testid="resetAutocompleteButton"
                                    className="input--search__button input--search__button--reset"
                                    onClick={this.onClear.bind(this)}
                                />
                                {this.renderSpinner()}
                            </div>
                            <button
                                type="button"
                                aria-label="Avbryt"
                                className="input--search__submit pad-override button button--flat u-float-right"
                                data-testid="cancelAutocompleteButton"
                                onClick={this.onCancel.bind(this)}>
                                Avbryt
                            </button>
                        </div>
                        {this.renderOptions()}
                    </div>
                ) : (
                    <>
                        {this.renderOptions()}
                        {this.renderSpinner()}
                    </>
                )}
            </div>
        );
    }
}
